// Vuex module: relbase
// Story 4.1 — AC5: Persistencia de borrador DTE para prevenir pérdida de datos

const defaultDraft = () => ({
  // Encabezados
  otId: null,
  otTitle: '',
  customerId: null,
  relbaseCustomerId: null,   // AC2: ID del cliente en Relbase (para validar sincronización)
  rutRecep: '',
  rznSocRecep: '',
  giroRecep: '',
  dirRecep: '',
  cmnaRecep: '',
  strCity: '',
  contactRecep: '',
  glosa: '',
  payment: 0,
  // Selectores Relbase
  bodega: null,
  vendedor: null,
  canalVenta: null,
  formaPago: null,
  terminosPago: null,
  // Líneas de detalle (cada línea puede tener relbase_product_id)
  lines: [],
  // Descuento global sobre neto (%)
  globalDiscount: 0,
  // AC1: request_id para idempotencia (UUID persistido por borrador/OT)
  requestId: null,
  // Story 4.5: Fecha de vencimiento del documento
  endDate: null,
  // Metadata del borrador
  savedAt: null,
})

const state = () => ({
  currentDteDraft: defaultDraft(),
  issuedDocuments: [],      // historial de documentos emitidos para la OT activa
  relbaseSellers: [],
  relbaseWarehouses: [],
  relbaseSalesChannels: [],
  relbasePaymentMethods: [],
  relbasePaymentTerms: [],
})

const mutations = {
  SET_DTE_DRAFT(state, draft) {
    state.currentDteDraft = { ...state.currentDteDraft, ...draft, savedAt: new Date().toISOString() }
  },
  RESET_DTE_DRAFT(state) {
    state.currentDteDraft = defaultDraft()
  },
  SET_DRAFT_HEADERS(state, headers) {
    const { rutRecep, rznSocRecep, giroRecep, dirRecep, cmnaRecep, strCity, contactRecep, glosa, payment,
            customerId, relbaseCustomerId, otId, otTitle } = headers
    if (rutRecep !== undefined) state.currentDteDraft.rutRecep = rutRecep
    if (rznSocRecep !== undefined) state.currentDteDraft.rznSocRecep = rznSocRecep
    if (giroRecep !== undefined) state.currentDteDraft.giroRecep = giroRecep
    if (dirRecep !== undefined) state.currentDteDraft.dirRecep = dirRecep
    if (cmnaRecep !== undefined) state.currentDteDraft.cmnaRecep = cmnaRecep
    if (strCity !== undefined) state.currentDteDraft.strCity = strCity
    if (contactRecep !== undefined) state.currentDteDraft.contactRecep = contactRecep
    if (glosa !== undefined) state.currentDteDraft.glosa = glosa
    if (payment !== undefined) state.currentDteDraft.payment = payment
    if (customerId !== undefined) state.currentDteDraft.customerId = customerId
    if (relbaseCustomerId !== undefined) state.currentDteDraft.relbaseCustomerId = relbaseCustomerId
    if (otId !== undefined) state.currentDteDraft.otId = otId
    if (otTitle !== undefined) state.currentDteDraft.otTitle = otTitle
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  // AC1: Asegura que el borrador tenga un request_id único (idempotencia)
  ENSURE_DRAFT_REQUEST_ID(state) {
    if (!state.currentDteDraft.requestId) {
      // Genera un UUID v4 compatible con todos los navegadores
      state.currentDteDraft.requestId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        c => {
          const r = (Math.random() * 16) | 0
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
        }
      )
    }
  },
  RESET_DRAFT_REQUEST_ID(state) {
    state.currentDteDraft.requestId = null
  },
  SET_DRAFT_LINES(state, lines) {
    state.currentDteDraft.lines = lines
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  ADD_DRAFT_LINE(state, line) {
    state.currentDteDraft.lines.push(line)
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  UPDATE_DRAFT_LINE(state, { index, line }) {
    if (index >= 0 && index < state.currentDteDraft.lines.length) {
      state.currentDteDraft.lines.splice(index, 1, { ...state.currentDteDraft.lines[index], ...line })
      state.currentDteDraft.savedAt = new Date().toISOString()
    }
  },
  REMOVE_DRAFT_LINE(state, index) {
    state.currentDteDraft.lines.splice(index, 1)
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  SET_DRAFT_SELECTORS(state, { bodega, vendedor, canalVenta, formaPago, terminosPago }) {
    if (bodega !== undefined) state.currentDteDraft.bodega = bodega
    if (vendedor !== undefined) state.currentDteDraft.vendedor = vendedor
    if (canalVenta !== undefined) state.currentDteDraft.canalVenta = canalVenta
    if (formaPago !== undefined) state.currentDteDraft.formaPago = formaPago
    if (terminosPago !== undefined) state.currentDteDraft.terminosPago = terminosPago
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  SET_DRAFT_GLOBAL_DISCOUNT(state, discount) {
    state.currentDteDraft.globalDiscount = parseFloat(discount) || 0
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  // Story 4.5: Fecha de vencimiento
  SET_DRAFT_END_DATE(state, endDate) {
    state.currentDteDraft.endDate = endDate || null
    state.currentDteDraft.savedAt = new Date().toISOString()
  },
  SET_ISSUED_DOCUMENTS(state, docs) {
    state.issuedDocuments = docs
  },
  SET_RELBASE_SELLERS(state, sellers) {
    state.relbaseSellers = sellers
  },
  SET_RELBASE_WAREHOUSES(state, warehouses) {
    state.relbaseWarehouses = warehouses
  },
  SET_RELBASE_SALES_CHANNELS(state, channels) {
    state.relbaseSalesChannels = channels
  },
  SET_RELBASE_PAYMENT_METHODS(state, methods) {
    state.relbasePaymentMethods = methods
  },
  SET_RELBASE_PAYMENT_TERMS(state, terms) {
    state.relbasePaymentTerms = terms
  },
}

const getters = {
  dteDraft: state => state.currentDteDraft,
  draftLines: state => state.currentDteDraft.lines,
  draftHasPendingData: state => state.currentDteDraft.savedAt !== null,
  issuedDocuments: state => state.issuedDocuments,
  relbaseSellers: state => state.relbaseSellers,
  relbaseWarehouses: state => state.relbaseWarehouses,
  relbaseSalesChannels: state => state.relbaseSalesChannels,
  relbasePaymentMethods: state => state.relbasePaymentMethods,
  relbasePaymentTerms: state => state.relbasePaymentTerms,
}

const actions = {
  saveDraftHeaders({ commit }, headers) {
    commit('SET_DRAFT_HEADERS', headers)
  },
  saveDraftLines({ commit }, lines) {
    commit('SET_DRAFT_LINES', lines)
  },
  saveDraftSelectors({ commit }, selectors) {
    commit('SET_DRAFT_SELECTORS', selectors)
  },
  resetDraft({ commit }) {
    commit('RESET_DTE_DRAFT')
    commit('SET_ISSUED_DOCUMENTS', [])
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
}
